import { useTheme } from '@/contexts/ThemeContext';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Button, LuxModal, LuxModalBody, LuxModalFooter, LuxModalHeader } from '@components/ui';
import React from 'react';

interface ConfirmDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  itemName?: string;
  isDeleting?: boolean;
  errorMessage?: string | null;
}

const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirmer la suppression',
  message = 'Êtes-vous sûr de vouloir supprimer cet élément ?',
  itemName,
  isDeleting = false,
  errorMessage = null,
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const handleClose = () => {
    if (!isDeleting) onClose();
  };

  const handleConfirm = () => {
    onConfirm();
  };

  if (!isOpen) return null;

  return (
    <LuxModal
      isOpen={isOpen}
      onClose={handleClose}
      size="sm"
      variant="danger"
      closeOnBackdrop={!isDeleting}
      closeOnEscape={!isDeleting}
      showCloseButton
    >
      <LuxModalHeader variant="danger" showCloseButton={!isDeleting} onClose={handleClose}>
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 p-2 rounded-lg bg-red-100 dark:bg-red-500/20">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Action irréversible</p>
          </div>
        </div>
      </LuxModalHeader>

      <LuxModalBody
        className="space-y-4"
        style={{ backgroundColor: isDark ? undefined : '#fafafa' }}
      >
        <p className="text-sm text-gray-600 dark:text-slate-300">{message}</p>

        {itemName && (
          <div className="p-3 bg-gray-50 dark:bg-slate-700/30 rounded-lg border border-gray-200 dark:border-slate-600">
            <p className="text-sm font-medium text-gray-900 dark:text-slate-200">{itemName}</p>
          </div>
        )}

        <div className="p-3 bg-red-50 dark:bg-red-900/30 rounded-lg border border-red-200 dark:border-red-800">
          <p className="text-sm text-red-800 dark:text-red-300">
            <strong>Attention :</strong> Cette action est irréversible.
          </p>
        </div>

        {errorMessage && (
          <div className="p-3 bg-red-100 dark:bg-red-900/50 rounded-lg border border-red-300 dark:border-red-800">
            <p className="text-sm text-red-900 dark:text-red-200 font-medium">⚠️ {errorMessage}</p>
          </div>
        )}
      </LuxModalBody>

      <LuxModalFooter align="right">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={handleClose}
          disabled={isDeleting}
        >
          Annuler
        </Button>
        <Button
          type="button"
          variant="danger"
          size="sm"
          onClick={handleConfirm}
          disabled={isDeleting}
          loading={isDeleting}
        >
          {isDeleting ? 'Suppression...' : 'Supprimer'}
        </Button>
      </LuxModalFooter>
    </LuxModal>
  );
};

export default ConfirmDeleteModal;
